import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useSystemUsers } from '@/shared/hooks/useSystemUsers';
import { cn } from '@/lib/utils';
import { useSimpleAvatarUrl } from '@/domains/billing/hooks/teamtasks/useAvatarUrl';
import type { EmployeeData } from '@shared/types/system-users';
import type { Timestamp } from '@shared/types/submission';
import { X } from 'lucide-react';
import type { ContentBlock, MentionBlock } from './types/messageContent';
import { extractMentionEmails } from './types/messageContent';

interface MentionUser {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  team_name?: string;
  cached_avatar_url?: string;
  cached_thumbnail_url?: string;
  avatar_cache_status?: 'pending' | 'cached' | 'failed';
  avatar_cache_timestamp?: Date | Timestamp;
}

interface StructuredMentionInputProps {
  value: ContentBlock[];
  onChange: (blocks: ContentBlock[], mentions: string[]) => void;
  onSubmit: (blocks: ContentBlock[], mentions: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

interface MentionState {
  isOpen: boolean;
  searchTerm: string;
  selectedIndex: number;
  position: { top: number; left: number };
}

/**
 * StructuredMentionInput - A mention input that treats mentions as discrete objects (chips)
 *
 * Instead of storing "@Ross McGowan test" as text and parsing with regex,
 * this stores it as structured blocks:
 * [
 *   { type: 'mention', email: '...', displayName: 'Ross McGowan' },
 *   { type: 'text', content: ' test' }
 * ]
 *
 * Benefits:
 * - No regex ambiguity
 * - Clear boundaries between mentions and text
 * - Easy to style mentions as chips
 * - Easy to serialize/deserialize
 */
export interface StructuredMentionInputHandle {
  submit: () => void;
}

export const StructuredMentionInput = forwardRef<StructuredMentionInputHandle, StructuredMentionInputProps>(({
  value,
  onChange,
  onSubmit,
  placeholder = "Type a message... (use @ to mention someone)",
  disabled = false,
  className
}, ref) => {
  const inputRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { users } = useSystemUsers();

  // Current editing state - the text being typed before next mention
  const [currentText, setCurrentText] = useState('');

  const [mentionState, setMentionState] = useState<MentionState>({
    isOpen: false,
    searchTerm: '',
    selectedIndex: 0,
    position: { top: 0, left: 0 }
  });

  // Convert system users to mention users
  const mentionUsers: MentionUser[] = users.map(user => ({
    id: user.firebase_uid || user.id || '',
    name: user.employee_data?.name || user.email?.split('@')[0] || 'Unknown User',
    email: user.email || '',
    avatar: user.employee_data?.avatar,
    team_name: user.employee_data?.team_name,
    cached_avatar_url: user.employee_data?.cached_avatar_url,
    cached_thumbnail_url: user.employee_data?.cached_thumbnail_url,
    avatar_cache_status: user.employee_data?.avatar_cache_status,
    avatar_cache_timestamp: user.employee_data?.avatar_cache_timestamp,
  })).filter(user => user.id && user.email);

  // Filter users based on search term
  const filteredUsers = mentionUsers.filter(user =>
    user.name.toLowerCase().includes(mentionState.searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(mentionState.searchTerm.toLowerCase())
  ).slice(0, 10);

  // Handle text input changes
  const handleTextChange = (e: React.FormEvent<HTMLDivElement>) => {
    const text = e.currentTarget.textContent || '';
    setCurrentText(text);

    // Check if we're typing after an @ symbol
    const lastAtIndex = text.lastIndexOf('@');

    if (lastAtIndex !== -1 && lastAtIndex === text.length - 1) {
      // Just typed @, open dropdown
      setMentionState({
        isOpen: true,
        searchTerm: '',
        selectedIndex: 0,
        position: { top: 0, left: 0 }
      });
    } else if (lastAtIndex !== -1 && lastAtIndex < text.length - 1) {
      // Typing after @
      const textAfterAt = text.slice(lastAtIndex + 1);
      // Only keep dropdown open if no spaces (simple mention typing)
      if (!textAfterAt.includes(' ')) {
        setMentionState({
          isOpen: true,
          searchTerm: textAfterAt,
          selectedIndex: 0,
          position: { top: 0, left: 0 }
        });
      } else {
        setMentionState(prev => ({ ...prev, isOpen: false }));
      }
    } else {
      setMentionState(prev => ({ ...prev, isOpen: false }));
    }
  };

  // Add a mention chip
  const addMention = (user: MentionUser) => {
    // Get text before the @ and after the search term
    const lastAtIndex = currentText.lastIndexOf('@');
    const textBeforeAt = currentText.slice(0, lastAtIndex);
    const searchTerm = mentionState.searchTerm;
    const textAfterSearch = currentText.slice(lastAtIndex + 1 + searchTerm.length);

    // Create new blocks array
    const newBlocks: ContentBlock[] = [...value];

    // Add any text before @ as text block
    if (textBeforeAt.trim()) {
      newBlocks.push({ type: 'text', content: textBeforeAt });
    }

    // Add mention block
    const mentionBlock: MentionBlock = {
      type: 'mention',
      userId: user.id,
      email: user.email,
      displayName: user.name
    };
    newBlocks.push(mentionBlock);

    // Set current text to whatever was after the search term (if any)
    setCurrentText(textAfterSearch);
    setMentionState(prev => ({ ...prev, isOpen: false }));

    // Notify parent
    const mentions = extractMentionEmails(newBlocks);
    onChange(newBlocks, mentions);

    // Update input display and focus back
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.textContent = textAfterSearch;
        inputRef.current.focus();
        // Move cursor to end
        const range = document.createRange();
        const selection = window.getSelection();
        if (inputRef.current.childNodes.length > 0) {
          range.setStart(inputRef.current.childNodes[0], textAfterSearch.length);
        } else {
          range.selectNodeContents(inputRef.current);
        }
        range.collapse(true);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }, 0);
  };

  // Remove a mention chip
  const removeMention = (index: number) => {
    const newBlocks = value.filter((_, i) => i !== index);
    const mentions = extractMentionEmails(newBlocks);
    onChange(newBlocks, mentions);
  };

  // Handle keyboard navigation in dropdown
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!mentionState.isOpen) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      } else if (e.key === 'Backspace' && currentText === '' && value.length > 0) {
        // Backspace with empty current text - remove last block
        e.preventDefault();
        removeMention(value.length - 1);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setMentionState(prev => ({
          ...prev,
          selectedIndex: Math.min(prev.selectedIndex + 1, filteredUsers.length - 1)
        }));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setMentionState(prev => ({
          ...prev,
          selectedIndex: Math.max(prev.selectedIndex - 1, 0)
        }));
        break;
      case 'Enter':
      case 'Tab':
        e.preventDefault();
        if (filteredUsers[mentionState.selectedIndex]) {
          addMention(filteredUsers[mentionState.selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setMentionState(prev => ({ ...prev, isOpen: false }));
        break;
    }
  };

  // Handle form submission
  const handleSubmit = () => {
    // Parse any @mentions in the current text before submitting
    const finalBlocks: ContentBlock[] = [...value];

    if (currentText.trim()) {
      // Parse the current text for any @mentions that weren't selected from dropdown
      const parsedBlocks = parseTextForMentions(currentText);
      finalBlocks.push(...parsedBlocks);
    }

    // Only submit if there's content
    if (finalBlocks.length > 0) {
      const mentions = extractMentionEmails(finalBlocks);
      onSubmit(finalBlocks, mentions);

      // Clear after submit
      setCurrentText('');
      if (inputRef.current) {
        inputRef.current.textContent = '';
      }
    }
  };

  useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  // Parse text for @mentions and convert to blocks
  const parseTextForMentions = (text: string): ContentBlock[] => {
    const blocks: ContentBlock[] = [];
    // Match @Name (handles McGowan, MacLeod, etc.)
    const mentionRegex = /@([A-Z][a-zA-Z]*(?:\s+[A-Z][a-zA-Z]*)*?)(?=\s|$|[.!?,;:])/g;

    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = mentionRegex.exec(text)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        const textBefore = text.slice(lastIndex, match.index);
        if (textBefore) {
          blocks.push({ type: 'text', content: textBefore });
        }
      }

      // Try to find the user
      const mentionedName = match[1].trim();
      const user = mentionUsers.find(u =>
        u.name.toLowerCase() === mentionedName.toLowerCase()
      );

      if (user) {
        // Found user - create mention block
        blocks.push({
          type: 'mention',
          userId: user.id,
          email: user.email,
          displayName: user.name
        });
      } else {
        // User not found - keep as text
        blocks.push({ type: 'text', content: match[0] });
      }

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      const remainingText = text.slice(lastIndex);
      if (remainingText) {
        blocks.push({ type: 'text', content: remainingText });
      }
    }

    // If no mentions found, return as single text block
    if (blocks.length === 0 && text) {
      blocks.push({ type: 'text', content: text });
    }

    return blocks;
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setMentionState(prev => ({ ...prev, isOpen: false }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative flex-1">
      <div
        className={cn(
          'w-full px-2 py-2 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 min-h-[40px] max-h-[200px] overflow-y-auto flex flex-wrap gap-1 items-center',
          disabled && 'opacity-50 cursor-not-allowed bg-gray-50',
          className
        )}
      >
        {/* Render existing blocks as chips */}
        {value.map((block, index) => {
          if (block.type === 'mention') {
            return (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded-md text-sm font-medium"
              >
                @{block.displayName}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => removeMention(index)}
                    className="hover:bg-blue-200 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            );
          } else {
            return (
              <span key={index} className="inline text-sm">
                {block.content}
              </span>
            );
          }
        })}

        {/* Editable text input area */}
        <div
          ref={inputRef}
          contentEditable={!disabled}
          onInput={handleTextChange}
          onKeyDown={handleKeyDown}
          className="flex-1 min-w-[100px] outline-none text-sm"
          data-placeholder={value.length === 0 && !currentText ? placeholder : ''}
          style={{
            minHeight: '24px',
          }}
          suppressContentEditableWarning
        />
      </div>

      {/* Mention Dropdown */}
      {mentionState.isOpen && filteredUsers.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-[9999] bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto min-w-[280px] mt-1"
          style={{
            top: '100%',
            left: 0,
            right: 0
          }}
        >
          {filteredUsers.map((user, index) => (
            <div
              key={user.id}
              className={cn(
                'flex items-center space-x-3 px-3 py-2 cursor-pointer hover:bg-gray-50',
                index === mentionState.selectedIndex && 'bg-purple-50 border-l-2 border-purple-500'
              )}
              onClick={() => addMention(user)}
            >
              <MentionUserAvatar user={user} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">
                  {user.name}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {user.email}
                </div>
                {user.team_name && (
                  <div className="text-xs text-gray-400 truncate">
                    {user.team_name}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
});

// Avatar component for mention dropdown
const MentionUserAvatar: React.FC<{ user: MentionUser }> = ({ user }) => {
  const employeeData: EmployeeData | undefined = {
    name: user.name,
    avatar: user.avatar,
    cached_avatar_url: user.cached_avatar_url,
    cached_thumbnail_url: user.cached_thumbnail_url,
    avatar_cache_status: user.avatar_cache_status,
    avatar_cache_timestamp: user.avatar_cache_timestamp instanceof Date
      ? user.avatar_cache_timestamp
      : user.avatar_cache_timestamp
        ? new Date(user.avatar_cache_timestamp.seconds * 1000)
        : undefined,
  };

  const avatarUrl = useSimpleAvatarUrl(employeeData, true);

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="flex-shrink-0">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={user.name}
          className="w-8 h-8 rounded-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center text-sm font-medium">
          {getInitials(user.name)}
        </div>
      )}
    </div>
  );
};

